import React, { useState } from "react";
import { Mail, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function FreeEnquiryForm({ listing, company }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field = "w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white focus:outline-none focus:border-[#264839]";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/listings/${listing.slug || listing.id}/enquiry`, form);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : "Could not send your enquiry");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mt-8 bg-[#F0EFEA] border-l-4 border-[#264839] p-5 text-sm text-[#4A5550]" data-testid="free-enquiry-success">
        <div className="flex items-center gap-2 font-medium text-[#11211A]"><CheckCircle2 className="w-4 h-4 text-[#264839]" /> Enquiry sent to {company}</div>
        <p className="mt-1">They'll be in touch shortly. We've emailed you a confirmation at <span className="font-medium text-[#11211A]">{form.email}</span>.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 bg-[#F0EFEA] p-5" data-testid="free-enquiry-block">
      {!open ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <p className="text-sm text-[#4A5550]">Interested in {company}'s products? Send them an enquiry — we'll pass it on and they'll reply to you directly.</p>
          <button type="button" onClick={() => setOpen(true)} className="btn-primary text-xs shrink-0" data-testid="free-enquiry-open"><Mail className="w-3.5 h-3.5" /> Send Enquiry</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="free-enquiry-form">
          <div className="font-serif text-lg text-[#11211A]">Send an enquiry to {company}</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="free-enquiry-name" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Your email" className={field} data-testid="free-enquiry-email" />
          </div>
          <input value={form.company} onChange={set("company")} placeholder="Hotel / company (optional)" className={field} data-testid="free-enquiry-company" />
          <textarea required minLength={10} rows={4} value={form.message} onChange={set("message")} placeholder="What are you looking for? Quantities, timing, delivery location…" className={field} data-testid="free-enquiry-message" />
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-[#7D8C85]">Your enquiry is delivered to the supplier via Accommodation Suppliers. You'll get a confirmation email.</p>
            <div className="flex gap-2 shrink-0">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs" data-testid="free-enquiry-cancel">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="free-enquiry-submit"><Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send Enquiry"}</button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
